/* Measure the one header and reveal focused rail links; native code owns popups/cart. */
if (!customElements.get('otf-header-observer')) {
  class OtfHeaderObserver extends HTMLElement {
    connectedCallback() {
      this.headerSection = this.closest('.otf-header-section');
      this.header = this.headerSection?.querySelector('[data-otf-top-header]');
      if (!this.header) return;
      // CartPopup binds the initial anchor in its constructor. Rebind only a
      // replacement header anchor, using the same native click/Space contract.
      const cartAnchor = this.header.querySelector('#cart-icon-bubble');
      const cartPopup = document.querySelector('cart-popup');
      if (OtfHeaderObserver.cartAnchor && OtfHeaderObserver.cartAnchor !== cartAnchor && typeof cartPopup?.setHeaderCartIconAccessibility === 'function') {
        cartPopup.setHeaderCartIconAccessibility();
      }
      OtfHeaderObserver.cartAnchor = cartAnchor;
      this.rowSlot = this.header.querySelector(".otf-top-header__row-slot");
      this.row = this.header.querySelector(".otf-top-header__row");
      this.compactEnabled = Boolean(this.rowSlot && this.row && this.header.dataset.sticky === "true");
      if (this.compactEnabled) this.headerSection.dataset.otfCompactReady = "true";
      this.measure = () => {
        const utility = this.header.querySelector('.otf-top-header__utility-strip');
        const height = this.header.getBoundingClientRect().height;
        const utilityHeight = utility?.getBoundingClientRect().height || 0;
        const stickyHeight = this.compactEnabled ? this.row.getBoundingClientRect().height : height - utilityHeight;
        const setChanged = (style, name, value) => {
          if (style.getPropertyValue(name) !== value || style.getPropertyPriority(name)) style.setProperty(name, value);
        };
        setChanged(this.headerSection.style, '--otf-header-utility-height', `${Math.ceil(utilityHeight)}px`);
        setChanged(document.documentElement.style, '--header-height', `${Math.ceil(height)}px`);
        setChanged(document.documentElement.style, '--otf-header-sticky-height', `${Math.ceil(stickyHeight)}px`);
        setChanged(document.documentElement.style, '--bottom-fixed-navigation', '0px');
        if (document.body.classList.contains('navigation-bottom-fixed')) document.body.classList.remove('navigation-bottom-fixed');
      };
      this.onRailFocus = event => {
        const link = event.target.closest('a');
        const rail = event.currentTarget;
        if (!link) return;
        const railRect = rail.getBoundingClientRect();
        const linkRect = link.getBoundingClientRect();
        if (linkRect.left < railRect.left) rail.scrollLeft -= railRect.left - linkRect.left + 8;
        else if (linkRect.right > railRect.right) rail.scrollLeft += linkRect.right - railRect.right + 8;
      };
      this.rail = this.header.querySelector('.otf-department-rail');
      this.rail?.addEventListener('focusin', this.onRailFocus);
      this.photoRail = this.headerSection?.querySelector('.otf-drawer-photo-strip__track');
      this.photoRail?.addEventListener('focusin', this.onRailFocus);
      this.observer = new ResizeObserver(this.measure);
      this.observer.observe(this.header);
      this.syncCompact = () => {
        if (!this.compactEnabled) return;
        const compact = this.rowSlot.getBoundingClientRect().top <= 0;
        if ((this.headerSection.dataset.otfCompact === 'true') !== compact) {
          this.headerSection.dataset.otfCompact = String(compact);
          this.measure();
        }
      };
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.syncCompact(); });
      };
      this.onFocus = event => {
        if (!this.compactEnabled || event.target.id === 'MainContent' || event.target.closest('.rt-theme-popup, [role="dialog"], .otf-top-header__row')) return;
        requestAnimationFrame(() => {
          if (!event.target.isConnected || document.activeElement !== event.target) return;
          this.syncCompact();
          const rect = event.target.getBoundingClientRect();
          const bottom = this.headerSection.dataset.otfCompact === 'true' ? this.row.getBoundingClientRect().bottom + 8 : 0;
          if (rect.height && rect.top < bottom && rect.bottom > 0) window.scrollBy({ top: rect.top - bottom, behavior: 'instant' });
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      window.addEventListener('pageshow', this.onScroll);
      document.addEventListener('focusin', this.onFocus);
      this.observer.observe(this.row || this.header);
      this.measure();
      this.syncCompact();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      window.removeEventListener('pageshow', this.onScroll);
      document.removeEventListener('focusin', this.onFocus);
      if (this.headerSection) { delete this.headerSection.dataset.otfCompactReady; delete this.headerSection.dataset.otfCompact; }
      this.rail?.removeEventListener('focusin', this.onRailFocus);
      this.photoRail?.removeEventListener('focusin', this.onRailFocus);
    }
  }
  customElements.define('otf-header-observer', OtfHeaderObserver);
}

/* Drawer-only media adapter. Native popup/navigation code retains ownership. */
if (!customElements.get('otf-drawer-spotlights')) {
  class OtfDrawerSpotlights extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.popup = this.closest('#menu-drawer[data-popup-type="mobile-navigation"]');
      this.track = this.querySelector('.otf-drawer-photo-strip__track');
      this.inner = this.popup?.querySelector('.popup--inner');
      if (!this.popup || !this.track || !this.inner) return;
      this.bound = true;
      this.pageHidden = false;
      this.cards = [...this.track.querySelectorAll('[data-otf-spotlight]')];
      const retained = this.states;
      this.states = new Map();
      this.listeners = [];
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.connection = navigator.connection;
      this.cards.forEach(card => {
        const video = card.querySelector('video[data-otf-drawer-video]');
        const button = card.querySelector('[data-otf-drawer-toggle]');
        const media = card.querySelector('.otf-drawer-photo-strip__media');
        if (!video || !button || !media || !video.dataset.src) return;
        const previous = retained?.get(card);
        const state = { card, video, button, media, token: 0, paused: previous?.paused || false, failed: previous?.failed || false, pending: false, playing: false, explicit: false };
        this.states.set(card, state);
        video.muted = true;
        button.hidden = false;
        this.listen(video, 'error', () => { state.failed = true; this.release(state); this.update(state, 'Clip unavailable. The campaign photo is shown.'); });
        this.update(state);
      });
      this.onSync = () => this.schedule();
      this.onRailScroll = () => { this.preferred = null; this.schedule(); };
      this.listen(this.track, 'scroll', this.onRailScroll, { passive: true });
      this.listen(this.inner, 'scroll', this.onSync, { passive: true });
      this.listen(window, 'scroll', this.onSync, { passive: true });
      this.listen(window, 'resize', this.onSync);
      this.listen(document, 'visibilitychange', this.onSync);
      this.listen(window, 'pagehide', () => { this.pageHidden = true; this.sync(); });
      this.listen(window, 'pageshow', () => { this.pageHidden = false; this.schedule(); });
      this.listen(this, 'click', event => {
        const button = event.target.closest('[data-otf-drawer-toggle]');
        const card = button?.closest('[data-otf-spotlight]');
        const state = this.states.get(card);
        if (!state || !this.contains(button)) return;
        this.preferred = card;
        if (state.playing || state.pending) {
          state.paused = true;
          state.explicit = false;
          this.release(state);
        } else {
          state.paused = false;
          state.failed = false;
          state.explicit = true;
          this.sync();
        }
      });
      this.listen(this.track, 'focusin', event => {
        const card = event.target.closest('[data-otf-spotlight]');
        if (!card) return;
        const rail = this.track.getBoundingClientRect();
        const rect = card.getBoundingClientRect();
        if (rect.left < rail.left) this.track.scrollLeft -= rail.left - rect.left + 4;
        else if (rect.right > rail.right) this.track.scrollLeft += rect.right - rail.right + 4;
        this.preferred = card;
        this.schedule();
      });
      this.listen(document, 'otf:story-play', event => {
        if (event.detail?.source === this) return;
        this.states.forEach(state => {
          if (!state.playing && !state.pending) return;
          state.paused = true;
          state.explicit = false;
          this.release(state);
        });
      });
      this.listen(document, 'shopify:section:unload', event => {
        if (event.target === this || event.target.contains?.(this)) this.disconnectedCallback();
      });
      this.onPreference = () => {
        this.states.forEach(state => { state.explicit = false; });
        this.schedule();
      };
      this.motion.addEventListener?.('change', this.onPreference);
      this.connection?.addEventListener?.('change', this.onPreference);
      this.mutations = new MutationObserver(this.onSync);
      this.mutations.observe(this.popup, { attributes: true, attributeFilter: ['class', 'inert', 'aria-hidden'] });
      this.mutations.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['active-popup', 'class', 'inert', 'aria-hidden'] });
      if ('IntersectionObserver' in window) {
        this.intersections = new IntersectionObserver(this.onSync, { threshold: [0, 0.6, 1] });
        this.cards.forEach(card => this.intersections.observe(card));
      }
      this.schedule();
    }
    listen(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.listeners.push(() => target.removeEventListener(type, handler, options));
    }
    schedule() {
      if (!this.bound || this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.sync(); });
    }
    isOpen() {
      if (!this.isConnected || this.pageHidden || document.hidden || document.visibilityState === 'hidden') return false;
      if (document.body.getAttribute('active-popup') !== 'mobile-navigation') return false;
      if (this.popup.classList.contains('hide-popup') || this.closest('[inert]') || this.popup.getAttribute('aria-hidden') !== 'false') return false;
      if (document.querySelector('.rt-theme-popup:not(.hide-popup):not([data-popup-type="mobile-navigation"]), .overlay-navigation-panel__popup:not(.hide-popup)')) return false;
      const style = getComputedStyle(this.popup);
      return style.display !== 'none' && style.visibility !== 'hidden';
    }
    visibleRatio(card) {
      const media = card.querySelector('.otf-drawer-photo-strip__media');
      if (!media || getComputedStyle(media).visibility === 'hidden') return 0;
      const rect = media.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return 0;
      const track = this.track.getBoundingClientRect();
      const inner = this.inner.getBoundingClientRect();
      const topbar = this.popup.querySelector('.otf-header-drawer__topbar')?.getBoundingClientRect();
      const left = Math.max(rect.left, track.left, inner.left, 0);
      const right = Math.min(rect.right, track.right, inner.right, document.documentElement.clientWidth);
      const top = Math.max(rect.top, track.top, inner.top, topbar?.bottom || 0, 0);
      const bottom = Math.min(rect.bottom, track.bottom, inner.bottom, window.innerHeight);
      return Math.max(0, right - left) * Math.max(0, bottom - top) / (rect.width * rect.height);
    }
    sync() {
      if (!this.bound) return;
      const open = this.isOpen();
      const ratios = new Map(this.cards.map(card => [card, open ? this.visibleRatio(card) : 0]));
      const eligible = this.cards.filter(card => ratios.get(card) >= 0.6);
      const active = eligible.includes(this.preferred) ? this.preferred : eligible.sort((a, b) => ratios.get(b) - ratios.get(a) || a.getBoundingClientRect().left - b.getBoundingClientRect().left)[0];
      this.states.forEach(state => {
        const visible = open && active === state.card;
        if (!visible) {
          state.explicit = false;
          this.release(state);
          return;
        }
        const automatic = Boolean(this.intersections) && !this.motion.matches && !this.connection?.saveData;
        if (state.paused || state.failed || (!automatic && !state.explicit)) {
          this.release(state);
          return;
        }
        if (!state.pending && !state.playing) this.play(state);
      });
    }
    play(state) {
      const token = ++state.token;
      state.pending = true;
      state.video.muted = true;
      if (!state.video.hasAttribute('src')) state.video.src = state.video.dataset.src;
      this.update(state);
      let playback;
      try { playback = state.video.play(); } catch (error) { playback = Promise.reject(error); }
      Promise.resolve(playback).then(() => {
        if (!this.bound || state.token !== token) {
          if (!state.pending && !state.playing) state.video.pause();
          return;
        }
        if (!this.isOpen() || this.visibleRatio(state.card) < 0.6) { this.release(state); return; }
        state.pending = false;
        state.playing = true;
        this.update(state);
        document.dispatchEvent(new CustomEvent('otf:story-play', { detail: { source: this, video: state.video } }));
      }).catch(() => {
        if (!this.bound || state.token !== token) return;
        state.failed = true;
        state.explicit = false;
        this.release(state);
        this.update(state, 'The campaign photo is shown. Select Play to try the clip.');
      });
    }
    release(state) {
      ++state.token;
      state.pending = false;
      state.playing = false;
      state.video.pause();
      state.video.muted = true;
      if (state.video.hasAttribute('src')) {
        state.video.removeAttribute('src');
        state.video.load();
      }
      this.update(state);
    }
    update(state, message) {
      const action = state.playing || state.pending ? 'Pause' : 'Play';
      state.card.dataset.otfDrawerPlaying = String(state.playing);
      state.button.setAttribute('aria-label', `${action} ${state.card.dataset.otfSpotlight} clip`);
      state.button.setAttribute('aria-pressed', String(state.playing || state.pending));
      const status = state.card.querySelector('[data-otf-drawer-status]');
      const text = message || (state.playing ? 'Muted clip playing.' : 'Campaign photo shown.');
      if (status && status.textContent !== text) status.textContent = text;
    }
    disconnectedCallback() {
      if (!this.bound) return;
      this.bound = false;
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.states.forEach(state => { this.release(state); state.button.hidden = true; });
      this.listeners.forEach(remove => remove());
      this.motion.removeEventListener?.('change', this.onPreference);
      this.connection?.removeEventListener?.('change', this.onPreference);
      this.mutations?.disconnect();
      this.intersections?.disconnect();
    }
  }
  customElements.define('otf-drawer-spotlights', OtfDrawerSpotlights);
}
